// agoraLocalTracks.js
'use strict';

import { player as playerDebug } from '../resource/debug.js';
import { getIsOnWebviewApp } from '../resource/getUserAgent.js';
import {
  requestWebviewMediaPermission,
  WEBVIEW_PERMISSION_STATUS,
} from '../resource/webviewMediaPermission.js';

const log = playerDebug.extend('log:agoraLocalTracks');
const errorLog = playerDebug.extend('error:agoraLocalTracks');

// Same code the Agora SDK sets on a getUserMedia denial; downstream permission
// handling (asker modal / preview denial callbacks) keys on it.
const PERMISSION_DENIED_ERROR_CODE = 'PERMISSION_DENIED';

const VIEWER_CAMERA_ENCODER_CONFIG = '720p';
const VIEWER_CAMERA_OPTIMIZATION_MODE = 'balanced';
const OPPOSITE_FACING_MODE = {
  user: 'environment',
  environment: 'user',
};

const LOCAL_TRACK_TYPE = {
  AUDIO: 'audio',
  VIDEO: 'video',
};

const LOCAL_TRACK_STEP = {
  GET_USER_MEDIA: 'getUserMedia',
};

const SWITCH_CAMERA_REASON = {
  SINGLE_CAMERA: 'single_camera',
  FAILED: 'failed',
};

const localTrackEntries = new Map();
const localTracksReleasedListeners = new Set();

const createLocalTrackEntry = channelId => ({
  channelId,
  tracks: { audioTrack: null, videoTrack: null },
  ensurePromises: {
    [LOCAL_TRACK_TYPE.AUDIO]: null,
    [LOCAL_TRACK_TYPE.VIDEO]: null,
  },
  // Last facing mode we successfully requested; browsers may keep reporting a
  // stale facingMode in track settings after a switch.
  requestedFacingMode: null,
});

/**
 * Get the channel's current local-track entry. Treat it as an opaque marker:
 * releasing and re-acquiring the tracks produces a new entry, so compare with
 * === to detect that the tracks changed hands while an operation awaited. Do
 * not read its internal fields.
 * @param {object} params
 * @param {string} params.channelId - Agora channel id.
 * @returns {object|null} Current entry when available.
 * @example
 * const entry = getLocalTrackEntry({ channelId: 'session-id' });
 */
const getLocalTrackEntry = ({ channelId } = {}) => {
  if (!channelId) return null;
  return localTrackEntries.get(channelId) ?? null;
};

const getOrCreateLocalTrackEntry = ({ channelId } = {}) => {
  if (!channelId) return null;
  let entry = localTrackEntries.get(channelId);
  if (!entry) {
    entry = createLocalTrackEntry(channelId);
    localTrackEntries.set(channelId, entry);
  }
  return entry;
};

const closeTrack = ({ track, trackType }) => {
  for (const operation of ['stop', 'close']) {
    try {
      track?.[operation]?.();
    } catch (error) {
      errorLog('closeTrack: failed', { trackType, operation, error });
    }
  }
};

const closeTracks = ({ audioTrack, videoTrack } = {}) => {
  closeTrack({ track: audioTrack, trackType: LOCAL_TRACK_TYPE.AUDIO });
  closeTrack({ track: videoTrack, trackType: LOCAL_TRACK_TYPE.VIDEO });
};

const createLocalTrack = async trackType => {
  // In the webview app the native side must approve device access before
  // getUserMedia; a bridge-level denial short-circuits with the same error
  // contract as a browser denial, and PROMPT (or a missing bridge handler)
  // falls through to getUserMedia unchanged. Guarded here so the browser
  // path stays synchronous up to getUserMedia (no added microtask).
  if (getIsOnWebviewApp()) {
    const permissionStatus = await requestWebviewMediaPermission({
      trackType,
    });
    if (permissionStatus === WEBVIEW_PERMISSION_STATUS.DENIED) {
      const error = new Error('Webview media permission denied');
      error.code = PERMISSION_DENIED_ERROR_CODE;
      throw error;
    }
  }
  if (trackType === LOCAL_TRACK_TYPE.AUDIO) {
    return window.AgoraRTC.createMicrophoneAudioTrack();
  }
  if (trackType === LOCAL_TRACK_TYPE.VIDEO) {
    return window.AgoraRTC.createCameraVideoTrack({
      encoderConfig: VIEWER_CAMERA_ENCODER_CONFIG,
      optimizationMode: VIEWER_CAMERA_OPTIMIZATION_MODE,
    });
  }
  return null;
};

const getCaptureFailure = ({ result, trackType }) =>
  result.status === 'rejected' ? { trackType, error: result.reason } : null;

/**
 * Get the local tracks for a channel.
 * @param {object} params
 * @param {string} params.channelId - Agora channel id.
 * @returns {{audioTrack: object|null, videoTrack: object|null}|null} Stored local tracks.
 * @example
 * const tracks = getLocalTracks({ channelId: 'session-id' });
 */
const getLocalTracks = ({ channelId } = {}) =>
  getLocalTrackEntry({ channelId })?.tracks ?? null;

/**
 * Check whether at least one local audio or video track is ready.
 * @param {object} params
 * @param {string} params.channelId - Agora channel id.
 * @returns {boolean} Whether any local track is available.
 * @example
 * const isReady = hasAnyLocalTrack({ channelId: 'session-id' });
 */
const hasAnyLocalTrack = ({ channelId } = {}) => {
  const tracks = getLocalTracks({ channelId });
  return !!(tracks?.audioTrack || tracks?.videoTrack);
};

/**
 * Return existing local tracks or acquire each missing media type. Concurrent
 * requests for the same media type share one in-flight request.
 * @param {object} params
 * @param {string} params.channelId - Agora channel id (= session id).
 * @returns {Promise<{tracks: {audioTrack: object|null, videoTrack: object|null}, failures: Array<{trackType: string, error: *}>, entry: object}|null>}
 * @example
 * const { tracks, failures } = await ensureLocalTracks({
 *   channelId: 'session-id',
 * });
 */
const ensureLocalTracks = async ({ channelId } = {}) => {
  const entry = getOrCreateLocalTrackEntry({ channelId });
  if (!entry) return null;
  const trackTypes = [LOCAL_TRACK_TYPE.AUDIO, LOCAL_TRACK_TYPE.VIDEO];
  const missingTrackTypes = trackTypes.filter(
    trackType => !entry.tracks[`${trackType}Track`]
  );
  if (!missingTrackTypes.length) {
    return { tracks: entry.tracks, failures: [], entry };
  }

  const results = await Promise.allSettled(
    missingTrackTypes.map(trackType =>
      ensureLocalTrack({ channelId, trackType })
    )
  );
  if (localTrackEntries.get(channelId) !== entry) return null;

  const failures = results
    .map((result, index) =>
      getCaptureFailure({ result, trackType: missingTrackTypes[index] })
    )
    .filter(Boolean);
  if (!hasAnyLocalTrack({ channelId })) {
    const error =
      failures[0]?.error ?? new Error('No local media track available');
    if (error && typeof error === 'object') {
      // Carry every track's failure so the catcher can tell which devices
      // were denied (e.g. both) rather than only the first one.
      Object.defineProperty(error, 'viewerCameraCaptureFailures', {
        value: failures,
        configurable: true,
      });
    }
    errorLog('ensureLocalTracks: failed', { channelId, error });
    throw error;
  }

  log('ensureLocalTracks: ready', { channelId });
  return { tracks: entry.tracks, failures, entry };
};

/**
 * Get or create one local audio or video track.
 * @param {object} params
 * @param {string} params.channelId - Agora channel id.
 * @param {'audio'|'video'} params.trackType - Track type to acquire.
 * @returns {Promise<object|null>} Local track when available.
 * @example
 * const audioTrack = await ensureLocalTrack({
 *   channelId: 'session-id',
 *   trackType: LOCAL_TRACK_TYPE.AUDIO,
 * });
 */
const ensureLocalTrack = async ({ channelId, trackType } = {}) => {
  const trackKey = `${trackType}Track`;
  const entry = getOrCreateLocalTrackEntry({ channelId });
  if (
    !entry ||
    !(trackKey in entry.tracks) ||
    !(trackType in entry.ensurePromises)
  ) {
    return null;
  }
  if (entry.tracks[trackKey]) return entry.tracks[trackKey];
  if (entry.ensurePromises[trackType]) {
    return entry.ensurePromises[trackType];
  }

  const ensurePromise = (async () => {
    try {
      const track = await createLocalTrack(trackType);
      const isStale = localTrackEntries.get(channelId) !== entry;
      if (isStale) {
        closeTracks({ [trackKey]: track });
        return null;
      }
      entry.tracks[trackKey] = track;
      log('ensureLocalTrack: ready', { channelId, trackType });
      return track;
    } catch (error) {
      if (error && typeof error === 'object') {
        error.viewerCameraStep = LOCAL_TRACK_STEP.GET_USER_MEDIA;
        error.viewerCameraTrackType = trackType;
        Object.defineProperty(error, 'viewerCameraTrackEntry', {
          value: entry,
          configurable: true,
        });
      }
      errorLog('ensureLocalTrack: failed', { channelId, trackType, error });
      throw error;
    } finally {
      if (entry.ensurePromises[trackType] === ensurePromise) {
        entry.ensurePromises[trackType] = null;
      }
    }
  })();

  entry.ensurePromises[trackType] = ensurePromise;
  return ensurePromise;
};

const notifyLocalTracksReleased = ({ channelId }) => {
  for (const listener of localTracksReleasedListeners) {
    try {
      listener({ channelId });
    } catch (error) {
      errorLog('notifyLocalTracksReleased: listener failed', {
        channelId,
        error,
      });
    }
  }
};

/**
 * Subscribe to successful local-track releases.
 * @param {object} params
 * @param {Function} params.listener - Called with the released channel id.
 * @returns {Function} Unsubscribe function.
 * @example
 * const unsubscribe = subscribeLocalTracksReleased({
 *   listener: ({ channelId }) => handleReleased(channelId),
 * });
 */
const subscribeLocalTracksReleased = ({ listener } = {}) => {
  if (typeof listener !== 'function') return () => {};
  localTracksReleasedListeners.add(listener);
  return () => localTracksReleasedListeners.delete(listener);
};

/**
 * Release device hardware and invalidate any in-flight track request.
 * @param {object} params
 * @param {string} params.channelId - Agora channel id (= session id).
 * @param {object|null} [params.entry] - Release only the matching entry.
 * @returns {void}
 * @example
 * const entry = getLocalTrackEntry({ channelId: 'session-id' });
 * releaseLocalTracks({ channelId: 'session-id', entry });
 */
const releaseLocalTracks = ({ channelId, entry } = {}) => {
  const currentEntry = getLocalTrackEntry({ channelId });
  if (!currentEntry || (entry !== undefined && currentEntry !== entry)) return;
  localTrackEntries.delete(channelId);
  try {
    closeTracks(currentEntry.tracks);
    log('releaseLocalTracks: done', { channelId });
  } finally {
    notifyLocalTracksReleased({ channelId });
  }
};

/**
 * Get the local video track for a channel.
 * @param {object} params
 * @param {string} params.channelId - Agora channel id.
 * @returns {object|null} Local video track when available.
 * @example
 * const videoTrack = getLocalVideoTrack({ channelId: 'session-id' });
 */
const getLocalVideoTrack = ({ channelId } = {}) =>
  getLocalTracks({ channelId })?.videoTrack ?? null;

/**
 * Get the current local video track settings.
 * @param {object} params
 * @param {string} params.channelId - Agora channel id.
 * @returns {MediaTrackSettings} Current video capture settings.
 * @example
 * const { facingMode } = getLocalVideoTrackSettings({
 *   channelId: 'session-id',
 * });
 */
const getLocalVideoTrackSettings = ({ channelId } = {}) =>
  getVideoTrackSettings(getLocalVideoTrack({ channelId }));

/**
 * Get the local audio track for a channel.
 * @param {object} params
 * @param {string} params.channelId - Agora channel id.
 * @returns {object|null} Local audio track when available.
 * @example
 * const audioTrack = getLocalAudioTrack({ channelId: 'session-id' });
 */
const getLocalAudioTrack = ({ channelId } = {}) =>
  getLocalTracks({ channelId })?.audioTrack ?? null;

/**
 * Enable or disable a local audio or video track.
 * @param {object} params
 * @param {string} params.channelId - Agora channel id.
 * @param {string} params.trackType - LOCAL_TRACK_TYPE.AUDIO or LOCAL_TRACK_TYPE.VIDEO.
 * @param {boolean} params.isEnabled - Whether the track should be enabled.
 * @returns {Promise<boolean>} Whether the setting was applied to a track.
 * @example
 * const isApplied = await setLocalTrackEnabled({
 *   channelId: 'session-id',
 *   trackType: LOCAL_TRACK_TYPE.VIDEO,
 *   isEnabled: false,
 * });
 */
const setLocalTrackEnabled = async ({
  channelId,
  trackType,
  isEnabled,
} = {}) => {
  const track = getLocalTracks({ channelId })?.[`${trackType}Track`];
  if (!track) return false;
  await track.setEnabled(isEnabled);
  return true;
};

const getCameras = async ({ channelId } = {}) => {
  try {
    return (await window.AgoraRTC.getCameras()) ?? [];
  } catch (error) {
    errorLog('getCameras: failed', { channelId, error });
    return null;
  }
};

const getVideoTrackSettings = videoTrack =>
  videoTrack?.getMediaStreamTrack?.()?.getSettings?.() ?? {};

const setNextCamera = async ({ channelId, videoTrack, cameras }) => {
  const entry = getLocalTrackEntry({ channelId });
  const { deviceId: currentDeviceId, facingMode } =
    getVideoTrackSettings(videoTrack);
  const currentFacingMode = entry?.requestedFacingMode ?? facingMode;
  const nextFacingMode = OPPOSITE_FACING_MODE[currentFacingMode];
  if (nextFacingMode) {
    try {
      await videoTrack.setDevice({ facingMode: nextFacingMode });
      if (entry) entry.requestedFacingMode = nextFacingMode;
      return;
    } catch (error) {
      log('setNextCamera: facingMode unavailable, using deviceId', {
        channelId,
        facingMode: nextFacingMode,
        error,
      });
    }
  }

  const nextCamera =
    cameras.find(camera => camera.deviceId !== currentDeviceId) || cameras[0];
  await videoTrack.setDevice(nextCamera.deviceId);
  // After a deviceId switch only the reported settings know the real facing;
  // overwrite (or clear) the memory so it cannot become the stale source.
  if (entry) {
    entry.requestedFacingMode =
      getVideoTrackSettings(videoTrack).facingMode ?? null;
  }
};

/**
 * Check whether the device has more than one camera.
 * @returns {Promise<boolean>} Whether multiple cameras are available.
 * @example
 * const canSwitchCamera = await getHasMultipleCameras();
 */
const getHasMultipleCameras = async () =>
  ((await getCameras())?.length ?? 0) >= 2;

/**
 * Switch the local video track to another camera.
 * @param {object} params
 * @param {string} params.channelId - Agora channel id.
 * @returns {Promise<{switched: boolean, reason: string|null}>} Switch result.
 * @example
 * const result = await switchLocalCamera({ channelId: 'session-id' });
 */
const switchLocalCamera = async ({ channelId } = {}) => {
  const videoTrack = getLocalVideoTrack({ channelId });
  if (!videoTrack) {
    return { switched: false, reason: SWITCH_CAMERA_REASON.FAILED };
  }

  const cameras = await getCameras({ channelId });
  if (!cameras) {
    return { switched: false, reason: SWITCH_CAMERA_REASON.FAILED };
  }
  if (cameras.length < 2) {
    return { switched: false, reason: SWITCH_CAMERA_REASON.SINGLE_CAMERA };
  }

  try {
    await setNextCamera({ channelId, videoTrack, cameras });
    const { deviceId, facingMode } = getVideoTrackSettings(videoTrack);
    log('switchLocalCamera: switched', {
      channelId,
      deviceId,
      facingMode,
    });
    return { switched: true, reason: null };
  } catch (error) {
    errorLog('switchLocalCamera: setDevice failed', { channelId, error });
    return { switched: false, reason: SWITCH_CAMERA_REASON.FAILED };
  }
};

/**
 * Whether the local camera preview should be mirrored. The front (user)
 * camera mirrors; the last successfully requested facing mode wins over the
 * possibly-stale reported settings.
 * @param {object} params
 * @param {string} params.channelId - Agora channel id.
 * @returns {boolean} Whether the local video preview should be mirrored.
 * @example
 * const isMirrored = getIsLocalVideoMirrored({ channelId: 'session-id' });
 */
const getIsLocalVideoMirrored = ({ channelId } = {}) => {
  const facingMode =
    getLocalTrackEntry({ channelId })?.requestedFacingMode ??
    getLocalVideoTrackSettings({ channelId })?.facingMode;
  return !facingMode || facingMode === 'user';
};

export {
  LOCAL_TRACK_TYPE,
  LOCAL_TRACK_STEP,
  SWITCH_CAMERA_REASON,
  getLocalTracks,
  hasAnyLocalTrack,
  ensureLocalTracks,
  ensureLocalTrack,
  getLocalTrackEntry,
  subscribeLocalTracksReleased,
  releaseLocalTracks,
  getLocalVideoTrack,
  getLocalVideoTrackSettings,
  getIsLocalVideoMirrored,
  getLocalAudioTrack,
  setLocalTrackEnabled,
  getHasMultipleCameras,
  switchLocalCamera,
};
